/**
 * Merchant Pro RBAC Sub-phase C1 — capability provider + hooks.
 *
 * Single-source consumer of `GET /api/owner/me/capabilities`. Built
 * for the C1 nav-filter swap; the same hooks are consumed by C2's
 * `<RequirePermission cap="…">` wraps inside the 7 shared
 * operational components.
 *
 * **C1 design discipline** — this module is intentionally
 *   • Pure consumer of `/api/owner/me/capabilities` (no hardcoded
 *     role-capability map).
 *   • Backward-compat with R-2: callers without capabilities loaded
 *     fall back to role-based gating. `OwnerLayout` reads the
 *     resolved capability set with a role-based fallback during the
 *     initial fetch.
 *   • No `role ===` JSX anywhere. Tier-1 invariant preserved.
 *
 * Tier-1 invariant (4c-AC#13 → R2-AC#6 → C1): consumers must use
 * `usePermission(cap)` or `<RequirePermission cap="…">` — never
 * `user.role === '…'`. This applies to ALL future consumers
 * (including the 7 shared operational components that C2 will wrap).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';

// --------------------------------------------------------------------
// Query key — co-located so cache invalidations land in one place.
// --------------------------------------------------------------------

export const CAPABILITIES_QUERY_KEY = ['rbac', 'me', 'capabilities'];

// --------------------------------------------------------------------
// useEffectiveCapabilities — hook
// --------------------------------------------------------------------

/**
 * Fetch the logged-in actor's effective capability set.
 *
 * Returns:
 *   { capabilities, capSet, resolution, isLoading, error, refetch }
 *
 *   • `capabilities` — array of capability ID strings (`[]` while
 *     loading or unauthenticated).
 *   • `capSet` — same data shaped as a `Set` for O(1) `has()` lookup.
 *   • `resolution` — full resolver payload from the backend, or
 *     `null` while loading. Useful for the OwnerLayout role-label.
 *   • `isLoading` — true on first fetch only (background revalidations
 *     keep stale data visible per react-query default).
 *
 * Caching: 5 min stale-time (the cap-set rarely changes mid-session,
 * and an explicit re-fetch can be triggered after a custom-role
 * change in C3 via `invalidateCapabilities()` below).
 */
export function useEffectiveCapabilities() {
  const { token, user } = useAuthStore();

  const query = useQuery({
    queryKey: CAPABILITIES_QUERY_KEY,
    queryFn: () => api.ownerMeCapabilities(token),
    enabled: !!token && !!user && user.role !== 'customer',
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const capabilities = query.data?.capabilities ?? [];
  const capSet = capabilities instanceof Set ? capabilities : new Set(capabilities);

  return {
    capabilities,
    capSet,
    resolution: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// --------------------------------------------------------------------
// usePermission — thin O(1) wrapper
// --------------------------------------------------------------------

/**
 * Returns true iff the actor's effective capability set contains
 * `cap`. While capabilities are loading, returns `false` (UI defaults
 * to hiding the gated element — safer than flashing it then hiding).
 */
export function usePermission(cap) {
  const { capSet } = useEffectiveCapabilities();
  if (!cap) return true;            // null cap = always-on (degenerate)
  return capSet.has(cap);
}

// --------------------------------------------------------------------
// <RequirePermission> — declarative wrapper
// --------------------------------------------------------------------

/**
 * Render `children` iff the actor has `cap`. Else renders `fallback`
 * (default: `null`).
 *
 *   <RequirePermission cap="reservation.confirm">
 *     <Button onClick={confirm} />
 *   </RequirePermission>
 *
 * **Sub-phase C1 surface** — defined here for downstream consumers,
 * but NOT yet inserted into the 7 shared operational components.
 * That insertion is the C2 deliverable. The component IS available
 * to any C1 consumer that wants to gate a single button (none in
 * scope for C1).
 *
 * **Tier-1 invariant** — uses capability checks ONLY. No
 * `user.role === '…'` patterns. Forward-compatible with C2/C3
 * without any consumer rewrites.
 */
export default function RequirePermission({ cap, fallback = null, children }) {
  const allowed = usePermission(cap);
  return allowed ? children : fallback;
}

// --------------------------------------------------------------------
// invalidateCapabilities — cache buster
// --------------------------------------------------------------------

/**
 * Force a re-fetch of the actor's effective capability set. Consumed
 * by C3 after a custom-role assignment change so the UI reflects the
 * new effective set without a hard refresh.
 */
export function useInvalidateCapabilities() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: CAPABILITIES_QUERY_KEY });
}
